import { Platform, type TextStyle } from 'react-native';

/**
 * Типографика Agapella на нативке: Lora 500 — display (логотип и крупные
 * эмоциональные заголовки), Nunito Sans 400/600/700 — весь интерфейс.
 * См. .claude/skills/typography (утверждённая система, перекрывает легаси
 * src/styles/typography.ts с Raleway + Inter).
 *
 * На вебе шрифты уже подключены CSS-переменными (--ag-font-ui / --ag-font-brand),
 * поэтому здесь возвращаются они, а нативные файлы на веб не грузятся.
 *
 * Важное про Android: для кастомного семейства система НЕ синтезирует
 * начертания — fontWeight поверх «NunitoSans-Regular» не даёт жирного, а на
 * части прошивок даёт фальшивый. Поэтому вес выбирается ФАЙЛОМ, а fontWeight
 * на нативке намеренно не проставляется.
 */

/**
 * Полный набор весов общей системы (400/600/700) плюс 500 для геймификации:
 * там набор «400 — текст, 500 — акценты» задан ТЗ «Все достижения» и
 * сознательно уже общего — решение владельца от 19.08.2026. Существующие
 * вызовы uiFont(400|500) не меняются; 600/700 добавлены для заголовков и
 * кнопок при переводе нативных экранов с системного шрифта на Nunito Sans.
 */
export type UiFontWeight = 400 | 500 | 600 | 700;

const NATIVE_UI_FAMILY: Record<UiFontWeight, string> = {
  400: 'NunitoSans-Regular',
  500: 'NunitoSans-Medium',
  600: 'NunitoSans-SemiBold',
  700: 'NunitoSans-Bold',
};

const NATIVE_DISPLAY_FAMILY = 'Lora-Medium';

const isWeb = Platform.OS === 'web';

// Сама загрузка живёт в useAgapellaFonts.ts: `expo-font` — ESM-пакет, и в
// цепочке импортов компонентов он ломает jest. Здесь остаются только чистые
// функции, которые вызываются из StyleSheet.

/** Web ships only 400/600/700 faces; a native-only 500 (gamification scale)
 * resolves to 600 on Web. 800/900 were removed in CORE-1118 — the browser was
 * silently substituting 700 for faces that do not exist.
 */
export type WebFontWeight = 400 | 500 | 600 | 700;

export function uiFont(weight: UiFontWeight = 400, webWeight?: WebFontWeight): TextStyle {
  if (isWeb) {
    const requestedWeight = webWeight ?? weight;
    const approvedWeight = requestedWeight === 500 ? 600 : requestedWeight;
    return {
      fontFamily: 'var(--ag-font-ui)',
      fontWeight: String(approvedWeight) as TextStyle['fontWeight'],
    };
  }
  return { fontFamily: NATIVE_UI_FAMILY[weight] };
}

/** Lora 500 — только логотип и display-заголовки. */
export function displayFont(): TextStyle {
  if (isWeb) return { fontFamily: 'var(--ag-font-brand)', fontWeight: '500' };
  return { fontFamily: NATIVE_DISPLAY_FAMILY };
}
